// Resolves any agency column id to a display value.
//
// Eleven columns predate this system and render rich cells of their own (status
// dropdowns, inline date pickers, the clickable company name). Everything else —
// the rest of the built-in catalog and every admin-created custom field — is
// read generically from the agent record here, so a new column needs no code.

import { CUSTOM_FIELD_PREFIX } from './agencyFieldConstants';

export function isCustomFieldId(id) {
  return String(id || '').startsWith(CUSTOM_FIELD_PREFIX);
}

export function customFieldKey(id) {
  return String(id || '').slice(CUSTOM_FIELD_PREFIX.length);
}

function readPath(record, path) {
  return String(path || '')
    .split('.')
    .reduce((value, part) => (value && typeof value === 'object' ? value[part] : undefined), record);
}

export function agencyFieldRawValue(fieldId, agent, fieldIndex) {
  if (!agent) return undefined;
  if (isCustomFieldId(fieldId)) {
    const values = agent.customFields && typeof agent.customFields === 'object' ? agent.customFields : {};
    return values[customFieldKey(fieldId)];
  }
  const definition = fieldIndex?.byId?.[fieldId];
  return readPath(agent, definition?.key || fieldId);
}

function formatDate(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const parsed = new Date(text.length <= 10 ? `${text}T00:00:00` : text);
  if (Number.isNaN(parsed.getTime())) return text;
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }).format(parsed);
}

export function agencyFieldDisplayValue(fieldId, agent, fieldIndex) {
  const definition = fieldIndex?.byId?.[fieldId];
  const raw = agencyFieldRawValue(fieldId, agent, fieldIndex);

  if (raw === null || raw === undefined || raw === '') return '';
  if (Array.isArray(raw)) {
    return raw
      .map((item) => (item && typeof item === 'object' ? item.name || item.label || '' : String(item || '')))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (raw && typeof raw === 'object') return String(raw.name || raw.label || '');

  const type = definition?.type;
  if (type === 'date') return formatDate(raw);
  if (type === 'number') {
    const number = Number(raw);
    return Number.isFinite(number) ? String(number) : String(raw);
  }
  return String(raw);
}

// Sorting wants the underlying value, not the formatted one, so dates and
// numbers order properly instead of alphabetically.
export function agencyFieldSortValue(fieldId, agent, fieldIndex) {
  const definition = fieldIndex?.byId?.[fieldId];
  const raw = agencyFieldRawValue(fieldId, agent, fieldIndex);
  if (raw === null || raw === undefined || raw === '') return null;
  if (definition?.type === 'number') {
    const number = Number(raw);
    return Number.isFinite(number) ? number : null;
  }
  if (definition?.type === 'date') {
    const timestamp = Date.parse(String(raw));
    return Number.isNaN(timestamp) ? null : timestamp;
  }
  return agencyFieldDisplayValue(fieldId, agent, fieldIndex);
}

export function buildFieldIndex(payload) {
  const catalog = Array.isArray(payload?.catalog) ? payload.catalog : [];
  const custom = Array.isArray(payload?.custom) ? payload.custom : [];
  const all = [...catalog, ...custom];
  return {
    catalog,
    custom,
    all,
    byId: Object.fromEntries(all.map((item) => [item.id, item])),
    types: Array.isArray(payload?.types) ? payload.types : [],
    maxCustomFields: Number(payload?.max_custom_fields) || 40,
  };
}

export function fieldLabel(fieldId, fieldIndex, fallback = '') {
  return fieldIndex?.byId?.[fieldId]?.label || fallback || fieldId;
}

// Groups the catalog for the "Add column" picker, custom fields last.
export function groupedAvailableFields(fieldIndex, usedIds) {
  const used = new Set(usedIds || []);
  const groups = new Map();
  (fieldIndex?.all || []).forEach((field) => {
    if (used.has(field.id)) return;
    const group = field.source === 'custom' ? 'Custom fields' : field.group || 'Other';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(field);
  });
  const ordered = [...groups.entries()].sort(([left], [right]) => {
    if (left === 'Custom fields') return 1;
    if (right === 'Custom fields') return -1;
    return left.localeCompare(right);
  });
  return ordered.map(([group, fields]) => ({ group, fields }));
}
